import { encounterCreatureField } from './array-types';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { readInventoryField, readInventoryRows } from '../janus-engine/compile/inventory-raw-readers';
import type { AppliedTouchedRow, AppliedRowIdentity } from '../janus-engine/transport/applied-frame';
import { cardEditStatusRows, savedCardEditFrame } from '../janus-engine/transport/card-edit-frame';
import { withAsyncDeadline } from './async-deadline';
import type { TruthPush } from './applied-truth-transport';
import type { SwarmCardProjection } from '../janus-engine/advancement/swarm-card-projection';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** Locate already-held inputs. Card saves must never fetch an enclosing entity. */
export function cardEditContext(payload: Row, owner: Window = window): { encounterId: string; actorId: string; hosts: Row[] } | null {
    const cache = (owner as unknown as { FateTide?: { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> } }).FateTide;
    const inventoryId = upper(payload.inventory_id), statusId = upper(payload.status_id ?? payload.status_effect_id);
    const creatureId = upper(payload.encounter_creature_id ?? payload.subject_id ?? (inventoryId || statusId ? '' : payload.caster_id));
    const hosts: Row[] = [];
    for (const value of cache?.casterCache?.values() ?? []) { const row = record(value); if (row) hosts.push(row); }
    for (const [eid, value] of cache?.encounterCache ?? []) {
        const encounter = record(value);
        if (!encounter) continue;
        const members = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr.map(record).filter((row): row is Row => !!row) : [];
        for (const member of members) if (!hosts.includes(member)) hosts.push(member);
        const holder = [...members, encounter].find(row => creatureId
            ? upper(encounterCreatureField(record(row.encounter_creatures), 'encounter_creature_id')) === creatureId
            : inventoryId ? hasInventory(row, inventoryId)
                : statusId ? [...cardEditStatusRows(row)].some(status => upper(readItemField(status, 'status_id')) === statusId) : false);
        if (holder) return { encounterId: upper(eid), actorId: upper(encounterCreatureField(record(holder.encounter_creatures), 'encounter_creature_id')) || upper(eid), hosts: [...hosts, encounter] };
    }
    return null; // Ordinary website authoring has no held table and retains its one ping-back.
}
function hasInventory(host: Row, id: string): boolean {
    return upper(readInventoryField(host, 'inventory_id')) === id
        || (['inventory', 'inventory_in_container', 'encounter_inventories_arr'] as const).some(key => readInventoryRows(host, key).some(row => hasInventory(row, id)));
}

/** Existing fetch callers retain their response contract; the acting table owns
 * the one operation and reconnect outbox. Ordinary authoring keeps its ping-back. */
export async function cardEditFetch(endpoint: string, init: RequestInit, swarm?: SwarmCardProjection): Promise<Response> {
    const payload = JSON.parse(String(init.body)) as Row;
    let owner: Window = window;
    try { if (window.parent !== window && window.parent?.ftPushAppliedTruth) owner = window.parent; } catch { /* cross-origin embed */ }
    const page = owner.document;
    const table = page?.querySelector?.('[data-ft3d-campaign-encounter-view], [data-ft-encounter-surface], #modify_encounter_creation_engine_bootstrap');
    if (page && !table) return withAsyncDeadline(signal => fetch(endpoint, { ...init, signal }), 6000);
    const context = cardEditContext(payload, owner);
    if (table && !context) throw new Error('card-edit:held-row-unavailable');
    if (!context) return withAsyncDeadline(signal => fetch(endpoint, { ...init, signal }), 6000);
    if (!owner.ftPushAppliedTruth) throw new Error('card-edit:truth-transport-unavailable');
    // A changed definition needs its native dependency delta, not a row pointer
    // with the previous definition's derived rules left attached. Keep unsupported
    // identity swaps explicit until that separate engine contract is available.
    if (payload.inventory_item_id) {
        const id = upper(payload.inventory_id);
        const prior = context.hosts.flatMap(host => inventoryNodes(host)).find(row => upper(readInventoryField(row, 'inventory_id')) === id);
        if (prior && upper(readInventoryField(prior, 'inventory_item_id')) !== upper(payload.inventory_item_id))
            throw new Error('card-edit:inventory-template-delta-unported');
    }
    if ('encounter_creature_monster_template_id' in payload) {
        const prior = context.hosts.find(row => upper(encounterCreatureField(record(row.encounter_creatures), 'encounter_creature_id')) === upper(payload.encounter_creature_id));
        if (prior && upper(encounterCreatureField(record(prior.encounter_creatures), 'encounter_creature_monster_template_id')) !== upper(payload.encounter_creature_monster_template_id)
            && !(swarm && upper(swarm.actorId) === upper(payload.encounter_creature_id)
                && upper(swarm.templateId) === upper(payload.encounter_creature_monster_template_id)))
            throw new Error('card-edit:creature-template-delta-unported');
    }
    const operationId = `card-edit:${crypto.randomUUID().toUpperCase()}`;
    const headers = new Headers(init.headers);
    headers.set('X-FT-Card-Edit', '1');
    headers.set('X-FT-Card-Operation', operationId);
    const { response, result } = await withAsyncDeadline(async signal => {
        const response = await fetch(endpoint, { ...init, body: JSON.stringify({ ...payload, relay: false }), headers, signal });
        const result = response.ok ? await response.clone().json() as { card_edit?: boolean; body?: string;
            rows?: AppliedTouchedRow[]; deletes?: AppliedRowIdentity[]; presentation_rows?: Row[]; base_revs?: Record<string, number>;
            pictures?: Row[]; cdn_url?: string } : null;
        return { response, result };
    }, 6000).catch(error => { throw new Error(record(error)?.name === 'TimeoutError'
        ? 'card-edit:save-timeout' : error instanceof Error ? error.message : 'card-edit:save-unavailable'); });
    if (!response.ok) return response;
    if (!result?.card_edit || !Array.isArray(result.rows)) throw new Error('card-edit:saved-delta-unavailable');
    const frame = savedCardEditFrame({ operation_id: operationId, encounter_id: context.encounterId,
        actor_id: context.actorId }, result.rows, context.hosts, result.deletes, result.pictures, result.cdn_url);
    if (swarm) {
        const id = upper(swarm.actorId), previous = frame.computed_delta?.[id] ?? { set: [], unset: [] };
        frame.computed_delta = { ...frame.computed_delta, [id]: { ...previous,
            set: [...previous.set, ...swarm.delta.set], unset: [...previous.unset, ...swarm.delta.unset],
            mirrors: { set: [...previous.mirrors?.set ?? [], ...swarm.delta.mirrors?.set ?? []],
                unset: [...previous.mirrors?.unset ?? [], ...swarm.delta.mirrors?.unset ?? []] } } };
        frame.inputs = { ...frame.inputs, definition_inputs: swarm.definitionInputs };
    }
    frame.inputs = { ...frame.inputs, card_edit_base_revs: result.base_revs ?? {} };
    const push: TruthPush = { type: 'janus_truth_presentation', command: { operation_id: operationId,
        schema: 'janus.card.edit.v1', encounter_id: context.encounterId, actor_id: context.actorId,
        truth_presentation_rows: result.presentation_rows ?? [] }, applied_frame: frame };
    // Register immutable frame bytes before marking this already-completed save.
    await withAsyncDeadline(() => Promise.resolve(owner.ftPushAppliedTruth!(push)), 16000)
        .catch(error => { throw new Error(record(error)?.name === 'TimeoutError' ? 'card-edit:apply-timeout' : String(error)); });
    await withAsyncDeadline(() => owner.ftSaveAppliedTruth?.(operationId, async () => response) ?? Promise.resolve(response), 16000);
    return new Response(result.body ?? '', { status: response.status,
        headers: { 'Content-Type': 'text/plain', 'X-FT-Card-Applied': '1' } });
}

function inventoryNodes(host: Row): Row[] {
    const result = readInventoryField(host, 'inventory_id') ? [host] : [];
    for (const key of ['inventory', 'inventory_in_container', 'encounter_inventories_arr'] as const)
        for (const row of readInventoryRows(host, key)) result.push(...inventoryNodes(row));
    return result;
}

export async function cardEditQuery(endpoint: string, target: string, payload: Row): Promise<string> {
    const response = await cardEditFetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const text = await response.text();
    const element = document.getElementById(target);
    if (element) element.innerHTML = text;
    if (!response.ok || /\bbg-(warning|danger)\b/.test(text)) throw new Error(text || `HTTP ${response.status}`);
    return text;
}
